// src/components/PricingTable.jsx
export const runtime = 'experimental-edge';
import React, { useState, useEffect, useMemo } from 'react';
import useUserStore from '../store/userStore';
import DebugPanel from './SuscriptionDebugPanel';
import BillingToggle from './BillingToggle';

// Оптимизированная функция для получения Google Analytics client_id
const getGAClientId = () => {
  return new Promise((resolve) => {
    console.log("Attempt to get GA Client ID");
    
    // Устанавливаем таймаут, чтобы не ждать вечно
    const timeout = setTimeout(() => {
      console.log("GA Client ID timeout");
      resolve(null);
    }, 2000);
    
    // 1. СНАЧАЛА проверяем cookie - это самый быстрый и надежный метод
    const getIdFromCookie = () => {
      try {
        // GA4 cookie format: _ga=GA1.1.1234567890.1234567890
        // или _ga=GA1.2.1234567890.1234567890
        const cookies = document.cookie.split('; ');
        
        // Ищем _ga cookie
        const gaCookie = cookies.find(row => row.startsWith('_ga='));
        if (gaCookie) {
          console.log("Found _ga cookie:", gaCookie);
          // Извлекаем ID после "GA1.X."
          const parts = gaCookie.split('.');
          if (parts.length >= 4) {
            const clientId = `${parts[2]}.${parts[3]}`;
            console.log("Extracted GA client_id from cookie:", clientId);
            clearTimeout(timeout);
            return clientId;
          }
        }
        
        // Проверяем также _ga_XXXXXXXX cookie (специфично для GA4)
        const ga4Cookie = cookies.find(row => row.startsWith('_ga_'));
        if (ga4Cookie) {
          console.log("Found GA4 cookie:", ga4Cookie);
          // GA4 specific cookie contains session data
          // Format: _ga_XXXXXXXX=GS1.1.1234567890.1.1.1234567890.0.0.0
          const ga4parts = ga4Cookie.split('.');
          if (ga4parts.length >= 3) {
            // Используем timestamp как client_id если нет другого
            const clientId = ga4parts[2];
            console.log("Extracted client_id from GA4 cookie:", clientId);
            clearTimeout(timeout);
            return clientId;
          }
        }
      } catch (e) {
        console.error("Error parsing cookies:", e);
      }
      return null;
    };

    // Сначала пробуем получить ID из cookie (это работает мгновенно)
    const cookieClientId = getIdFromCookie();
    if (cookieClientId) {
      clearTimeout(timeout);
      resolve(cookieClientId);
      return;
    }
    
    // 2. Если cookie не найден, пробуем gtag API
    if (typeof window !== 'undefined' && window.gtag) {
      try {
        console.log("Trying gtag API with:", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID);
        window.gtag('get', process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID, 'client_id', (clientId) => {
          if (clientId) {
            console.log("Successfully got client_id from gtag:", clientId);
            clearTimeout(timeout);
            resolve(clientId);
          } else {
            console.log("gtag returned null client_id");
            // Не делаем resolve здесь, продолжаем проверку других методов
          }
        });
      } catch (e) {
        console.error("Error using gtag API:", e);
        // Продолжаем выполнение - может сработает другой метод
      }
    } 
    // 3. Пробуем Universal Analytics (устаревший, но может быть доступен)
    else if (typeof window !== 'undefined' && window.ga) {
      try {
        console.log("Trying Universal Analytics API");
        const tracker = window.ga.getAll()[0];
        if (tracker) {
          const clientId = tracker.get('clientId');
          console.log("Got client_id from UA tracker:", clientId);
          clearTimeout(timeout);
          resolve(clientId);
          return;
        }
      } catch (e) {
        console.error("Error with UA API:", e);
        // Продолжаем выполнение
      }
    }

    // Если мы дошли сюда и timeout еще не сработал, значит мы все еще ждем ответа от gtag API
    // Timeout очистит себя и вернет null если ничего не найдено
  });
};

// Компонент для отображения спиннера загрузки
const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// CheckMark component
const CheckMark = () => (
  <svg className="text-purple-600 mr-2 mt-1 flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// X Mark component
const XMark = () => (
  <svg className="text-gray-400 mr-2 mt-1 flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PricingTable = ({ initialSubscriptionData, initialAuth }) => {
  const [isYearly, setIsYearly] = useState(false);
  const [isTrialMode, setIsTrialMode] = useState(false); // Изменено на false, чтобы отключить триал по умолчанию
  const [trialDays, setTrialDays] = useState(14); // Default 14 days trial
  const [loading, setLoading] = useState(false);
  // Состояния загрузки для разных кнопок
  const [loadingFree, setLoadingFree] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [loadingPro, setLoadingPro] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(initialSubscriptionData);
  const [initialized, setInitialized] = useState(true); // Изменено на true, чтобы избежать показа плейсхолдера
  
  // Get user state and functions from the store
  const { 
    isLoggedIn, 
    fetchWithTokenRefresh, 
    isSubscriptionActive, 
    subscription,
    fetchUserProfile,
    setUserManually
  } = useUserStore();

  // Немедленная инициализация данных из SSR если они есть
  useEffect(() => {
    if (initialAuth?.isAuthenticated && initialAuth?.user) {
      // Проверяем, не является ли initialSubscriptionData.plan ID подписки
      const planToStore = initialSubscriptionData?.plan?.startsWith('sub_') 
        ? initialSubscriptionData?.plan_name || 'premium' // fallback to 'premium' if plan_name missing
        : initialSubscriptionData?.plan || '';
        
      setUserManually({
        ...initialAuth.user,
        isSubscriptionActive: initialSubscriptionData?.active || false,
        subscription: planToStore,
      });
    }
    
    // Сразу устанавливаем начальные данные о подписке
    if (initialSubscriptionData) {
      setSubscriptionStatus(initialSubscriptionData);
    }
    
    // Инициализация всегда считается завершенной
    setInitialized(true);
  }, []);

  // Fetch subscription status again if needed
  useEffect(() => {
    async function fetchDataIfNeeded() {
      // Делаем запрос только если пользователь авторизован И 
      // у нас еще нет данных о подписке
      if (isLoggedIn && 
          (!initialSubscriptionData || 
           !subscriptionStatus)) {
        await fetchSubscriptionStatus();
      }
    }
    
    fetchDataIfNeeded();
  }, [isLoggedIn]);

  // Вычисляем текущий план подписки пользователя
  const userPlan = useMemo(() => {
    // Получаем значения из store и из status
    const fromStore = subscription?.toLowerCase() || '';
    const fromStatus = subscriptionStatus?.plan?.toLowerCase() || '';
    
    // ИСПРАВЛЕНИЕ: Проверяем, не является ли fromStore ID подписки Stripe
    // ID подписки Stripe всегда начинаются с "sub_"
    const storeIsStripeId = fromStore.startsWith('sub_');
    
    // Если в store ID подписки, используем значение из status
    let resultPlan = storeIsStripeId ? fromStatus : (fromStore || fromStatus || '');
    
    // Добавляем логирование для отладки
    console.log('Current userPlan calculation:', {
      fromStore, 
      fromStatus,
      storeIsStripeId,
      resultPlan,
      subscriptionActive: isSubscriptionActive
    });
    
    return resultPlan;
  }, [subscription, subscriptionStatus, isSubscriptionActive]);

  // Проверяем, является ли указанный тип плана текущим планом пользователя
  const isCurrentPlan = (planType) => {
    // Если подписка НЕ активна, показываем только Free как текущий план
    if (!isSubscriptionActive) return planType.toLowerCase() === 'free';
    
    // Проверяем является ли planType текущим планом
    const normalizedPlanType = planType.toLowerCase().trim();
    const currentPlan = userPlan.toLowerCase().trim();
    
    console.log(`Checking plan: ${normalizedPlanType}, Current plan: ${currentPlan}, isActive: ${isSubscriptionActive}`);
    
    return normalizedPlanType === currentPlan;
  };

  // Fetch current subscription status
  const fetchSubscriptionStatus = async () => {
    if (!isLoggedIn) return;
    
    try {
      setLoading(true);
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
      
      if (!backendUrl) {
        console.error('Backend URL not defined in environment variables');
        setError('Configuration error: Missing backend URL. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Make the API request with error handling
      const response = await fetchWithTokenRefresh(
        `${backendUrl}/subscription/status`
      ).catch(err => {
        console.error('Network error fetching subscription:', err);
        throw new Error('Network error. Please check your connection and try again.');
      });
      
      // Handle non-OK response without throwing error
      if (!response.ok) {
        const status = response.status;
        const errorText = await response.text().catch(() => '');
        
        console.warn(`Subscription status API responded with ${status}:`, errorText);
        
        // Set subscription status to null but don't throw exception
        setSubscriptionStatus(null);
        
        // Only set user-facing error for severe failures
        if (status >= 500) {
          setError('Unable to load subscription information. The server might be experiencing issues.');
        } else if (status === 401 || status === 403) {
          setError('Your session may have expired. Please try logging in again.');
        } else {
          console.log('Non-critical subscription status error:', status);
          // Don't show error to user for 404 or other client errors
        }
        
        return;
      }
      
      // Process successful response
      const data = await response.json();
      setSubscriptionStatus(data);
      
      // Обновить глобальный стор с данными подписки для синхронизации
      if (data) {
        // ИСПРАВЛЕНИЕ: Убедимся, что сохраняем название плана, а не ID подписки
        const planToStore = data.plan_id?.startsWith('sub_') ? data.plan : data.plan;
        
        setUserManually({
          ...useUserStore.getState().user,
          isSubscriptionActive: data.active || false,
          subscription: planToStore || '',
        });
      }
      
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error in subscription status flow:', err);
      // Set a generic error message to avoid exposing technical details
      setError('Could not load your subscription information. Please try again later.');
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Get customer portal URL for subscription management
  const handleManageSubscription = async () => {
    try {
      // Отправляем событие клика на кнопку управления подпиской
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'subscription_button_click',
          'action_type': 'manage',
          'userID': useUserStore.getState().user?.id || '',
          'current_subscription': userPlan || 'none'
        });
      }
      
      setLoadingFree(true);
      setError(null);
      
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ return_url: window.location.href }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get customer portal URL');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Customer Portal
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Customer portal URL is missing in the response');
      }
    } catch (err) {
      console.error('Portal URL error:', err);
      setError(err.message || 'Failed to access subscription management. Please try again.');
    } finally {
      setLoadingFree(false);
    }
  };

  // Handle subscription checkout - с улучшенной передачей client_id
  const handleSubscribe = async (plan, interval, metadata = {}) => {
    if (!isLoggedIn) {
      const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${currentUrl}`;
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Получаем client_id из Google Analytics
      const gaClientId = await getGAClientId();
      
      // Выводим в консоль для отладки
      console.log("GA Client ID debug info:", {
        clientId: gaClientId || 'NULL',
        isGtagAvailable: typeof window !== 'undefined' && !!window.gtag,
        hasCookies: document.cookie.includes('_ga='),
        metadataBefore: metadata
      });
      
      // Добавляем client_id в метаданные, если он был получен
      if (gaClientId) {
        metadata.ga_client_id = gaClientId;
        console.log("Added GA client_id to metadata:", gaClientId);
      } else {
        console.log("No GA client_id found to add to metadata");
      }
      
      const trial_period_days = isTrialMode ? trialDays : null;
      const return_url = window.location.origin + '/pricing';
      
      // Добавляем метаданные в запрос
      const payload = {
        plan: plan,
        interval: interval,
        return_url: return_url,
        trial_period_days: trial_period_days,
        metadata: metadata
      };
      
      console.log("Sending subscription payload:", payload);
      
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/sub_checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { detail: errorText };
        }
        throw new Error(errorData.detail || 'Failed to create subscription checkout');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Checkout URL is missing in the response');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Обновленные функции-обработчики для разных планов с поддержкой метаданных
  const handlePremiumSubscribe = async () => {
    try {
      // Отправляем событие клика на кнопку подписки Premium
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'subscription_button_click',
          'plan_type': 'premium',
          'plan_interval': isYearly ? 'yearly' : 'monthly',
          'userID': useUserStore.getState().user?.id || '',
          'current_subscription': userPlan || 'none',
          'is_trial_offered': isTrialMode,
          'trial_days': isTrialMode ? trialDays : 0
        });
      }
      
      setLoadingPremium(true);
      if (isSubscriptionActive) {
        await handleManageSubscription();
      } else {
        // Собираем метаданные для передачи
        const metadata = {
          user_source: useUserStore.getState().user?.source || 'website',
          landing_page: window.location.pathname,
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
          user_email: useUserStore.getState().user?.email || '',
          user_subscription_status: 'new_subscription'
          // ga_client_id будет добавлен в handleSubscribe
        };
        
        await handleSubscribe('premium', isYearly ? 'yearly' : 'monthly', metadata);
      }
    } catch (err) {
      console.error('Premium subscribe error:', err);
      setError(err.message || "Не удалось обработать подписку Premium");
    } finally {
      setLoadingPremium(false);
    }
  };

  const handleProSubscribe = async () => {
    try {
      // Отправляем событие клика на кнопку подписки Pro
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'subscription_button_click',
          'plan_type': 'pro',
          'plan_interval': isYearly ? 'yearly' : 'monthly',
          'userID': useUserStore.getState().user?.id || '',
          'current_subscription': userPlan || 'none',
          'is_trial_offered': isTrialMode,
          'trial_days': isTrialMode ? trialDays : 0
        });
      }
      
      setLoadingPro(true);
      if (isSubscriptionActive) {
        await handleManageSubscription();
      } else {
        // Собираем метаданные для передачи
        const metadata = {
          user_source: useUserStore.getState().user?.source || 'website',
          landing_page: window.location.pathname,
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
          user_email: useUserStore.getState().user?.email || '',
          user_subscription_status: 'new_subscription'
          // ga_client_id будет добавлен в handleSubscribe
        };
        
        await handleSubscribe('pro', isYearly ? 'yearly' : 'monthly', metadata);
      }
    } catch (err) {
      console.error('Pro subscribe error:', err);
      setError(err.message || "Не удалось обработать подписку Pro");
    } finally {
      setLoadingPro(false);
    }
  };

  // Special handler for daily premium debug option - также обновлен с метаданными
  const handlePremiumDaily = async () => {
    try {
      // Отправляем событие клика на кнопку дневной подписки
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'subscription_button_click',
          'plan_type': 'premium',
          'plan_interval': 'daily',
          'userID': useUserStore.getState().user?.id || '',
          'current_subscription': userPlan || 'none',
          'is_trial_offered': false,
          'trial_days': 0
        });
      }
      
      setLoadingDaily(true);
      // Собираем метаданные для передачи
      const metadata = {
        user_source: useUserStore.getState().user?.source || 'website',
        landing_page: window.location.pathname,
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
        user_email: useUserStore.getState().user?.email || '',
        user_subscription_status: 'debug_daily',
        is_test: true
        // ga_client_id будет добавлен в handleSubscribe
      };
      
      await handleSubscribe('premium', 'daily', metadata);
    } catch (err) {
      console.error('Daily premium subscribe error:', err);
      setError(err.message || "Не удалось обработать дневную подписку");
    } finally {
      setLoadingDaily(false);
    }
  };

  return (
    <div className="w-full">
      {/* Error message if any */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded-md">
          {error}
        </div>
      )}
      
      {/* Используем вынесенный компонент BillingToggle */}
      <BillingToggle isYearly={isYearly} setIsYearly={setIsYearly} />
      
      {/* Pricing Cards - Full width with padding only on mobile */}
      <div className="px-4 sm:px-0 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free Plan */}
        <div className="border rounded-xl flex flex-col">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Free</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">$0</span>
              <div className="mt-2">
                <p className="text-gray-700">Basic access to icons</p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Free plan button - показываем разные варианты в зависимости от статуса подписки */}
              <button 
                className={`px-6 py-2 rounded-full font-medium flex items-center justify-center ${
                  isCurrentPlan('free') 
                  ? 'bg-white text-purple-600 border-2 border-purple-600' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                disabled={isCurrentPlan('free') || loadingFree}
                onClick={handleManageSubscription}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 isCurrentPlan('free') ? 'Your Current Plan' : 
                 'Manage Subscription'}
                 
                {loadingFree && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />Access to basic collections</li>
                <li className="flex items-start"><CheckMark />Free PNG format</li>
                <li className="flex items-start"><XMark />Limited Access to premium collections</li>
                <li className="flex items-start"><XMark />Icon customization</li>
                <li className="flex items-start"><XMark />Attribution required</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Premium Plan */}
        <div className="border rounded-xl flex flex-col relative overflow-hidden">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Premium</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">${isYearly ? '15' : '19'}</span>
              <span className="text-purple-600 ml-1">/mo</span>
              <div className="mt-2">
                <p className="text-gray-700">
                  {isYearly ? 'Billed annually ($180/year)' : 'Billed monthly'}
                </p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Premium plan button - для существующих подписок открываем портал, для новых - создаем */}
              <button 
                className={`px-6 py-2 rounded-full font-medium flex items-center justify-center ${
                  isCurrentPlan('premium') 
                  ? 'bg-white text-purple-600 border-2 border-purple-600' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                disabled={loading || loadingPremium}
                onClick={handlePremiumSubscribe}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 isCurrentPlan('premium') ? 'Your Current Plan' : 
                 isSubscriptionActive ? 'Manage Subscription' : 
                 isTrialMode ? `Try Free for ${trialDays} Day${trialDays === 1 ? '' : 's'}` : 'Subscribe Now'}
                 
                {loadingPremium && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />SVG vector format</li>
                <li className="flex items-start"><CheckMark />No attribution required</li>
                <li className="flex items-start"><CheckMark />Icon customization</li>
                <li className="flex items-start"><CheckMark />Full access to all collections</li>
                <li className="flex items-start"><XMark />Unlimited downloads</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pro Plan (ранее Advanced) */}
        <div className="border rounded-xl flex flex-col">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Pro</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">${isYearly ? '29' : '39'}</span>
              <span className="text-purple-600 ml-1">/mo</span>
              <div className="mt-2">
                <p className="text-gray-700">
                  {isYearly ? 'Billed annually ($348/year)' : 'Billed monthly'}
                </p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Pro plan button - для существующих подписок открываем портал, для новых - создаем */}
              <button 
                className={`px-6 py-2 rounded-full font-medium flex items-center justify-center ${
                  isCurrentPlan('pro') 
                  ? 'bg-white text-purple-600 border-2 border-purple-600' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                disabled={loading || loadingPro}
                onClick={handleProSubscribe}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 isCurrentPlan('pro') ? 'Your Current Plan' : 
                 isSubscriptionActive ? 'Manage Subscription' : 
                 isTrialMode ? `Try Free for ${trialDays} Day${trialDays === 1 ? '' : 's'}` : 'Subscribe Now'}
                 
                {loadingPro && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />All Premium features</li>
                <li className="flex items-start"><CheckMark />Unlimited icon downloads</li>
                <li className="flex items-start"><CheckMark />Priority support</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Debug Panel - будет отображаться только в режиме разработки */}
      <DebugPanel 
        isTrialMode={isTrialMode}
        setIsTrialMode={setIsTrialMode}
        trialDays={trialDays}
        setTrialDays={setTrialDays}
        handlePremiumDaily={handlePremiumDaily}
        loadingDaily={loadingDaily}
      />
    </div>
  );
};
 
export default PricingTable;